import { TokenClient, TokenSession, MissingAuthCookieError } from 'urbnTokenLib';

import ProfileClient from './ProfileClient';
import { AUTH_TOKEN_HEADER } from '../../constants';

import { fireDYEvent } from '../../utils/dyUtils';

/**
 * Login utility class for user sign-in, sign-up, and forgot password
 */
export default class LoginUtility {
    /**
     * Create an instance of this class.
     *
     * @param  {[TokenClient]} tokenClient   a TokenClient instance from urbnTokenLib
     * @param  {[ProfileClient]} profileClient  a ProfileClient instance
     */
    constructor(tokenClient, profileClient) {
        this._tokenClient = tokenClient;
        this._profileClient = profileClient;
    }

    /**
     * Signs in a user (gets an authorized token)
     *
     * @param  {[String]} emailAddress the user's email address
     * @param  {[String]} password     the user's password
     * @return {[Promise]}             resolves with a TokenSession object from urbnTokenLib,
     *                                 rejects with an error message string
     */
    signIn(emailAddress, password) {
        const additionalFields = {
            merge: true
        };

        const headers = {};

        return this._ensureTokenSession().then((tokenSession) => {
            headers[AUTH_TOKEN_HEADER] = tokenSession.authToken;
            fireDYEvent('login', emailAddress);

            return this._tokenClient.getAuthorizedToken(emailAddress, password, additionalFields, headers);
        });
    }

    // internal method that returns a promise that is either, the user's current token,
    // or a new guest token in case that expired
    _ensureTokenSession() {
        let tokenSession;
        let needsNewGuestToken = false;

        try {
            tokenSession = TokenSession.fromCookie();
        } catch (err) {
            if (err instanceof MissingAuthCookieError) {
                needsNewGuestToken = true;
            } else {
                throw err;
            }
        }

        let tokenSessionPromise;
        if (needsNewGuestToken) {
            tokenSessionPromise = this._tokenClient.getGuestToken();
        } else if (tokenSession.isNearlyExpired()) {
            tokenSessionPromise = this._tokenClient.attemptReauth();
        } else {
            tokenSessionPromise = Promise.resolve(tokenSession);
        }
        return tokenSessionPromise;
    }

    /**
     * Signs out a user. Blows away any auth token cookies the user may have
     * Does *not* recreate a GUEST token
     */
    signOut() {
        TokenSession.signOut();
    }

    /**
     * Creates an account for a user (does not log them in)
     *
     * @param  {[String]} emailAddress    the new user's email address
     * @param  {[String]} password        the new user's password
     * @param  {[String]} confirmPassword the new user's password confirmation
     * @param  {[String]} otherProfileData the new user's additional profile data
     * @return {[Promise]}                resolves with the new profile data,
     *                                    rejects with either an error rest.js HTTP response, or error message string
     */
    createAccount(emailAddress, password, confirmPassword, otherProfileData) {
        try {
            const tokenSession = TokenSession.fromCookie();
            const headers = {};

            headers[AUTH_TOKEN_HEADER] = tokenSession.authToken;

            return this._profileClient.createProfile(emailAddress, password, confirmPassword, otherProfileData, headers)
                .then((restResponse) => {
                    if (restResponse.status.code >= 200 && restResponse.status.code <= 299) {
                        fireDYEvent('signup', emailAddress);
                        return restResponse.entity;
                    }

                    console.error(
                        `Could not create profile. code: ${restResponse.status.code}, body: ${restResponse.entity}`
                    );
                    return Promise.reject(restResponse);
                })
                .catch((errorOrResponse) => {
                    return Promise.reject(errorOrResponse);
                });
        } catch (error) {
            return Promise.reject(error);
        }
    }

    /**
     * Sign up a new user (creates an account and then logs them in)
     *
     * @param  {[String]} emailAddress     the new user's email address
     * @param  {[String]} password         the new user's password
     * @param  {[String]} confirmPassword  the new user's password confirmation
     * @param  {[String]} otherProfileData the new user's additional profile data
     * @return {[Promise]}                 Resolves with the new user's TokenSession object,
     *                                     rejects with an error rest.js HTTP response or error message string
     */
    signUp(emailAddress, password, confirmPassword, otherProfileData = {}) {
        return this._ensureTokenSession().then((tokenSession) => {
            return this.createAccount(emailAddress, password, confirmPassword, otherProfileData)
                .then((profileData) => {
                    return this.signIn(emailAddress, password);
                })
                .catch((errorOrResponse) => {
                    return Promise.reject(errorOrResponse);
                });
        });
    }

    /**
     * Initiate forgot password process
     * @param  {[String]} emailAddress the email address of the user that forgot their password
     * @return {[Promise]}             resolves with success rest.js HTTP response,
     *                                 rejects with error rest.js HTTP response
     */
    forgotPassword(emailAddress) {
        return this._ensureTokenSession().then((tokenSession) => {
            return this._profileClient.forgotPassword(emailAddress);
        });
    }
}
